/**
 * ================================================================
 * API SERVICE: IPOBetaService
 * ================================================================
 *
 * @service          ipoBetaApi
 * @description      API service for IPO Beta management (details, bids, physical applications, documents)
 * @baseUrl          VITE_IPO_URL environment variable (default: http://192.168.119.248:9999)
 *
 * @endpoints
 * - GET    /api/v2/ipo/ipo-details - Get IPO details (current/upcoming)
 * - GET    /api/v2/ipo/dashboard-count - Get IPO dashboard summary counts
 * - GET    /api/v2/ipo/get-ipo-summary - Get IPO performance analytics summary
 * - GET    /api/v2/ipo/get-ipo-summary-details - Get IPO metric drilldown details
 * - GET    /api/v2/ipo/details/symbol - Get IPO details by symbol
 * - POST   /api/v2/ipo/bid/bidSubmit - Submit IPO bid
 * - GET    /api/v2/ipo/get-client-wise-bid-report - Get client-wise bid report
 * - PUT    /api/v2/ipo/update-ipo-mandate-status - Update bid status
 * - GET    /api/v2/ipo/fetch-client-details - Get client details
 * - GET    /kyc/clients/search - Get client list for autocomplete
 * - GET    /api/v2/ipo/sample-file-physical-csv - Download sample CSV
 * - POST   /api/v2/ipo/get-ipo-physical-pdf - Get physical IPO print
 * - POST   /api/v2/ipo/bid/bulk-applications - Bulk bid physical applications
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery, handleApiSuccess } from '@/lib/api/baseQuery';
import type {
  APIResponse,
  BidReportParams,
  BidReportResponse,
  BidSubmission,
  ClientDetailsResponse,
  ClientListParams,
  ClientListResponse,
  IPODetails,
  IPODetailsResponse,
  IPOResponse,
  UpiListResponse,
} from '@/types/ipo';
import type { BulkPhysicalSubmitResponse } from '@/types/ipo';
import type {
  MultipleIpoBidRequest,
  MultipleIpoBidResponse,
  MultipleIpoClientResponse,
} from '@/types/ipo/multiple';
import type { IPOAnalyticsApiResponse } from '@/types/ipo/performance';
import type { IPODashboardCountResponse } from '@/types/ipo/dashboard';
import type {
  IpoFinancialYearParams,
  IpoSummaryDetailsParams,
  IpoSummaryDetailsResponse,
} from '@/types/ipo/summaryDetails';

const IPO_URL = import.meta.env.VITE_IPO_URL || 'http://192.168.119.248:9999';

/**
 * Convert snake_case keys to camelCase recursively
 */
function toCamelCase(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  }

  const camelCaseObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      let camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

      const fieldMappings: { [key: string]: string } = {
        lotsize: 'lotSize',
        minprice: 'minPrice',
        cutoffprice: 'cutoffPrice',
        lotsizequantity: 'lotSizeQuantity',
        biddingstartdate: 'biddingStartDate',
        biddingenddate: 'biddingEndDate',
      };

      if (fieldMappings[key]) {
        camelKey = fieldMappings[key];
      }

      camelCaseObj[camelKey] = toCamelCase(obj[key]);
    }
  }
  return camelCaseObj;
}

/**
 * Convert a Base64 string into a Blob
 */
function base64ToBlob(base64: string, contentType = 'application/zip'): Blob {
  const cleaned = base64.replace(/\s/g, '');
  const byteCharacters = atob(cleaned);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}

export const ipoBetaApi = createApi({
  reducerPath: 'ipoBetaApi',
  baseQuery: createBaseQuery(IPO_URL),
  tagTypes: ['IPO', 'BidReport', 'ClientData', 'IPODetails', 'Watchlist', 'UPI'],
  endpoints: (builder) => ({
    // Get IPO dashboard count summary
    // Endpoint: GET /api/v2/ipo/dashboard-count
    getIpoBetaDashboardCount: builder.query<IPODashboardCountResponse, IpoFinancialYearParams>({
      query: ({ financeYear, source = 'ap' }) => ({
        url: `/api/v2/ipo/dashboard-count`,
        method: 'GET',
        params: { financialYear: financeYear, source },
      }),
      providesTags: ['IPO', 'Watchlist'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoBetaDashboardCount', true),
    }),

    // Get IPO performance analytics summary
    // Endpoint: GET /api/v2/ipo/get-ipo-summary
    getIpoBetaPerformanceAnalytics: builder.query<
      IPOAnalyticsApiResponse,
      IpoFinancialYearParams & { financialYear?: string | number }
    >({
      query: (params: any) => {
        let fy = params?.financialYear ?? params?.financeYear;
        if (!fy || isNaN(Number(fy))) {
          try {
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth();
            fy = currentMonth >= 3 ? currentYear : currentYear - 1;
          } catch {
            fy = 2026;
          }
        }
        const source = params?.source || 'ap';
        return {
          url: `/api/v2/ipo/get-ipo-summary`,
          method: 'GET',
          params: { financialYear: Number(fy), source },
        };
      },
      providesTags: ['IPO'],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('IpoBetaPerformanceAnalytics', true),
    }),

    // Get IPO summary details for a specific metric
    // Endpoint: GET /api/v2/ipo/get-ipo-summary-details
    getIpoBetaSummaryDetails: builder.query<IpoSummaryDetailsResponse, IpoSummaryDetailsParams>({
      query: (params: any) => {
        let fy = params?.financialYear ?? params?.financeYear;
        if (!fy || isNaN(Number(fy))) {
          fy = 2026;
        }
        const queryParams: Record<string, any> = {
          financialYear: Number(fy),
          source: params?.source || 'ap',
          pageSize: params?.pageSize ?? 10,
          pageNumber: params?.pageNumber ?? 1,
        };

        const filterVal = params?.FilterValue ?? params?.metric;
        const filterCol = params?.FilterColumn ?? params?.filterColumn;
        if (filterVal !== undefined && filterVal !== null && String(filterVal).trim() !== '') {
          queryParams.FilterValue = String(filterVal).trim();
          queryParams.FilterColumn = filterCol || 'status';
        }

        const searchVal = params?.SearchValue ?? params?.searchTerm;
        if (searchVal !== undefined && searchVal !== null && String(searchVal).trim() !== '') {
          queryParams.SearchValue = String(searchVal).trim();
        }

        return {
          url: `/api/v2/ipo/get-ipo-summary-details`,
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['IPODetails'],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('IpoBetaSummaryDetails', true),
    }),

    // Get IPO details (current/upcoming) - Mutation
    // Endpoint: GET /api/v2/ipo/ipo-details
    getIpoBetaDetails: builder.mutation<IPODetailsResponse, any>({
      query: (body) => {
        const p = body ? (body as any) : {};
        const params: Record<string, any> = {
          source: 'ap',
          status: p.status || p.condition || 'all',
        };
        if (p.pageNo !== undefined || p.pageNumber !== undefined) {
          params.pageNo = p.pageNo ?? p.pageNumber;
        }
        if (p.pageSize !== undefined) {
          params.pageSize = p.pageSize;
        }
        const searchVal = p.SearchValue ?? p.searchValue ?? p.search;
        if (searchVal !== undefined && searchVal !== null && String(searchVal).trim() !== '') {
          params.SearchValue = String(searchVal).trim();
        }
        const filterCol = p.FilterColumn ?? p.filterColumn;
        if (filterCol) {
          params.FilterColumn = filterCol;
        }
        const filterStat = p.FilterStatus ?? p.filterStatus;
        if (filterStat) {
          params.FilterStatus = filterStat;
        }
        return {
          url: `/api/v2/ipo/ipo-details`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: any) => {
        const transformed = toCamelCase(response);
        const rawData = transformed?.data;
        let list: any[] = [];
        if (Array.isArray(rawData)) {
          list = rawData;
        } else if (Array.isArray(rawData?.ipos)) {
          list = rawData.ipos;
        } else if (Array.isArray(transformed?.ipos)) {
          list = transformed.ipos;
        } else if (Array.isArray(transformed)) {
          list = transformed;
        }

        const meta = rawData?.metadata || transformed?.metadata || {};
        const totalRecords = Number(meta.totalRecords ?? list.length);
        const pageSize = Number(meta.pageSize ?? 5);
        const currentPage = Number(meta.currentPage ?? 1);
        const totalPages = Number(
          meta.totalPages ?? Math.max(1, Math.ceil(totalRecords / (pageSize || 5))),
        );

        return {
          ...transformed,
          data: list,
          metadata: {
            totalRecords,
            currentPage,
            pageSize,
            totalPages,
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('IpoBetaDetails', false),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {}
      },
    }),

    // Get IPO details (current/upcoming/all) - Query with automatic caching & refetch
    // Endpoint: GET /api/v2/ipo/ipo-details
    getIpoBetaDetailsList: builder.query<IPODetailsResponse, any>({
      query: (paramsObj) => {
        const p = paramsObj ? (paramsObj as any) : {};
        const params: Record<string, any> = {
          source: 'ap',
          status: p.status || p.condition || 'all',
        };
        if (p.pageNo !== undefined || p.pageNumber !== undefined) {
          params.pageNo = p.pageNo ?? p.pageNumber;
        }
        if (p.pageSize !== undefined) {
          params.pageSize = p.pageSize;
        }
        const searchVal = p.SearchValue ?? p.searchValue ?? p.search;
        if (searchVal !== undefined && searchVal !== null && String(searchVal).trim() !== '') {
          params.SearchValue = String(searchVal).trim();
        }
        const filterCol = p.FilterColumn ?? p.filterColumn;
        if (filterCol) {
          params.FilterColumn = filterCol;
        }
        const filterStat = p.FilterStatus ?? p.filterStatus;
        if (filterStat) {
          params.FilterStatus = filterStat;
        }
        const subType = p.subType ?? p.SubType;
        if (subType && subType !== 'ALL') {
          params.subType = subType;
        }
        return {
          url: `/api/v2/ipo/ipo-details`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['IPO'],
      transformResponse: (response: any) => {
        const transformed = toCamelCase(response);
        const rawData = transformed?.data;
        let list: any[] = [];
        if (Array.isArray(rawData)) {
          list = rawData;
        } else if (Array.isArray(rawData?.ipos)) {
          list = rawData.ipos;
        } else if (Array.isArray(transformed?.ipos)) {
          list = transformed.ipos;
        } else if (Array.isArray(transformed)) {
          list = transformed;
        }

        const meta = rawData?.metadata || transformed?.metadata || {};
        const totalRecords = Number(meta.totalRecords ?? list.length);
        const pageSize = Number(meta.pageSize ?? 5);
        const currentPage = Number(meta.currentPage ?? 1);
        const totalPages = Number(
          meta.totalPages ?? Math.max(1, Math.ceil(totalRecords / (pageSize || 5))),
        );

        return {
          ...transformed,
          data: list,
          metadata: {
            totalRecords,
            currentPage,
            pageSize,
            totalPages,
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('IpoBetaDetails', false),
    }),

    // Get specific IPO details by symbol
    // Endpoint: GET /api/v2/ipo/details/symbol
    getIpoBetaDetailsBySymbol: builder.mutation<APIResponse<IPOResponse>, { symbol: string }>({
      query: (body) => ({
        url: `/api/v2/ipo/details/symbol`,
        method: 'GET',
        params: { ...(body ? (body as any) : {}), source: 'ap' },
      }),
      invalidatesTags: ['IPO', 'IPODetails'],
      transformErrorResponse: createApiErrorTransformer('IpoBetaDetailsBySymbol', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBetaDetailsBySymbol', 'IPO symbol details loaded.', false);
        } catch {}
      },
    }),

    // Submit IPO bid
    // Endpoint: POST /api/v2/ipo/bid/bidSubmit
    submitBid: builder.mutation<any, { params: { client_code: string }; body: BidSubmission }>({
      query: ({ params, body }) => ({
        url: '/api/v2/ipo/bid/bidSubmit',
        method: 'POST',
        body,
        params: { ...params, source: 'ap' },
        responseHandler: async (response) => {
          if (body.amount > 500000) {
            return await response.blob();
          }
          return await response.json();
        },
      }),
      invalidatesTags: ['BidReport'],
      transformErrorResponse: createApiErrorTransformer('IpoBetaBidSubmit', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBetaBidSubmit', 'IPO Bid submitted successfully.', false);
        } catch {}
      },
    }),

    addIpoTransaction: builder.mutation<
      { statusCode?: number; message?: string; success?: boolean; data?: any },
      any
    >({
      query: (body) => ({
        url: `/api/v2/ipo/transactions/add`,
        method: 'POST',
        params: { source: 'ap' },
        body,
      }),
      invalidatesTags: ['BidReport'],
    }),

    addBulkIpoTransactionCsv: builder.mutation<
      { statusCode?: number; message?: string; success?: boolean; data?: any },
      any
    >({
      query: (body) => ({
        url: `/api/v2/ipo/transactions/addbulk-csv`,
        method: 'POST',
        params: { source: 'ap' },
        body,
      }),
      invalidatesTags: ['BidReport'],
    }),

    addMultipleIpoTransaction: builder.mutation<MultipleIpoBidResponse, MultipleIpoBidRequest[]>({
      query: (body) => ({
        url: '/api/v2/ipo/transactions/addbulk',
        method: 'POST',
        params: { source: 'ap' },
        body,
        // Keep partial results available when the API reports individual bid failures.
        validateStatus: (response) => response.status >= 200 && response.status < 300,
      }),
      invalidatesTags: ['BidReport', 'IPO'],
    }),

    getMultipleIpoClients: builder.query<MultipleIpoClientResponse, string>({
      query: (clientId) => ({
        url: '/api/v2/ipo/get-client-id',
        params: { source: 'ap', clientId },
      }),
      providesTags: ['ClientData'],
      transformResponse: (response: unknown) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('MultipleIpoClients', false),
    }),

    // Get client-wise bid report
    // Endpoint: GET /api/v2/ipo/get-client-wise-bid-report
    getBidReport: builder.query<BidReportResponse, BidReportParams>({
      query: (params) => ({
        url: '/api/v2/ipo/get-client-wise-bid-report',
        params: { ...params, source: 'ap' },
      }),
      providesTags: ['BidReport'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoBetaBidReport', false),
    }),

    // Update bid status
    // Endpoint: PUT /api/v2/ipo/update-ipo-mandate-status
    updateBidStatus: builder.mutation<any, any>({
      query: () => ({
        url: '/api/v2/ipo/update-ipo-mandate-status',
        method: 'PUT',
        params: { source: 'ap', exchange: '' },
      }),
      invalidatesTags: ['BidReport'],
      transformErrorResponse: createApiErrorTransformer('IpoBetaBidStatusUpdate', false),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBetaBidStatusUpdate', 'Bid status updated.', false);
        } catch {}
      },
    }),

    // Get client details
    // Endpoint: GET /api/v2/ipo/fetch-client-details
    getClientDetails: builder.query<
      ClientDetailsResponse,
      { client_code: string; source?: string }
    >({
      query: (params) => ({
        url: '/api/v2/ipo/fetch-client-details',
        params: { ...params, source: params.source || 'ap' },
      }),
      providesTags: ['ClientData'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoBetaClientDetails', true),
    }),

    // Get client list for autocomplete (shared KYC client search)
    getClientList: builder.query<ClientListResponse, ClientListParams>({
      query: (params) => ({
        url: `${import.meta.env.VITE_API_BASE_URL}/kyc/clients/search`,
        params: { searchQuery: params.code_part },
      }),
      providesTags: ['ClientData'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoBetaClientList', false),
    }),

    // Get client UPI IDs
    // Endpoint: GET /api/v2/ipo/upi?source=ap&clientId={clientId}
    getIpoUpiList: builder.query<
      UpiListResponse,
      { clientId: string; source?: string; limit?: number }
    >({
      query: ({ clientId, source = 'ap', limit }) => ({
        url: '/api/v2/ipo/upi',
        method: 'GET',
        params: { source, clientId, ...(limit ? { limit } : {}) },
      }),
      providesTags: ['UPI'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('GetIpoUpiList', false),
    }),

    // Add new UPI ID for client
    // Endpoint: POST /api/v2/ipo/upi/add?source=ap
    addIpoUpi: builder.mutation<
      any,
      { clientId: string; upiId: string; isDefault?: boolean; source?: string }
    >({
      query: ({ clientId, upiId, isDefault = false, source = 'ap' }) => ({
        url: '/api/v2/ipo/upi/add',
        method: 'POST',
        params: { source },
        body: { clientId, upiId, isDefault },
      }),
      invalidatesTags: ['UPI'],
      transformErrorResponse: createApiErrorTransformer('AddIpoUpi', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('AddIpoUpi', 'UPI ID added successfully.', true);
        } catch {}
      },
    }),

    // Set UPI ID as default
    // Endpoint: PATCH /api/v2/ipo/upi/set-default?source=ap
    setDefaultIpoUpi: builder.mutation<any, { clientId: string; upiId: string; source?: string }>({
      query: ({ clientId, upiId, source = 'ap' }) => ({
        url: '/api/v2/ipo/upi/set-default',
        method: 'PATCH',
        params: { source },
        body: { clientId, upiId },
      }),
      invalidatesTags: ['UPI'],
      transformErrorResponse: createApiErrorTransformer('SetDefaultIpoUpi', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('SetDefaultIpoUpi', 'Default UPI updated successfully.', true);
        } catch {}
      },
    }),

    // Remove UPI ID
    // Endpoint: DELETE /api/v2/ipo/upi?source=ap&id={id}
    deleteIpoUpi: builder.mutation<any, { id: number | string; clientId: string; source?: string }>(
      {
        query: ({ id, clientId, source = 'ap' }) => ({
          url: '/api/v2/ipo/upi',
          method: 'DELETE',
          params: { source, id },
          body: { clientId },
        }),
        invalidatesTags: ['UPI'],
        transformErrorResponse: createApiErrorTransformer('DeleteIpoUpi', true),
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled;
            handleApiSuccess('DeleteIpoUpi', 'UPI ID deleted successfully.', true);
          } catch {}
        },
      },
    ),

    // Download sample CSV
    // Endpoint: GET /api/v2/ipo/sample-file-physical-csv
    downloadSampleCsv: builder.mutation<Blob, any>({
      query: (params = {}) => ({
        url: '/api/v2/ipo/sample-file-physical-csv',
        params: { ...params, source: 'ap' },
        responseHandler: (response) => response.blob(),
      }),
      transformErrorResponse: createApiErrorTransformer('IpoBetaSampleCsv', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBetaSampleCsv', 'Sample CSV downloaded.', true);
        } catch {}
      },
    }),

    // Get physical IPO print (single)
    // Endpoint: POST /api/v2/ipo/get-ipo-physical-pdf
    getPhysicalPrint: builder.mutation<Blob, { body: FormData; params?: any }>({
      query: ({ body, params = {} }) => ({
        url: `${IPO_URL}/api/v2/ipo/get-ipo-physical-pdf`,
        method: 'POST',
        body,
        params: { ...params, source: 'ap' },
        responseHandler: async (response) => {
          const contentType = response.headers.get('content-type')?.toLowerCase() || '';

          if (contentType.includes('application/json')) {
            const json = await response.json().catch(() => null);
            if (json && (json.data || json.base64)) {
              const b64 = (json.data || json.base64) as string;
              const filename: string | undefined = json.filename || json.fileName;
              const ext = filename?.toLowerCase().endsWith('.pdf')
                ? 'application/pdf'
                : 'application/zip';
              return base64ToBlob(b64, ext);
            }
            return new Blob([JSON.stringify(json ?? {})], { type: 'application/json' });
          }

          const blob = await response.blob();
          return blob;
        },
      }),
      transformErrorResponse: createApiErrorTransformer('IpoBetaPhysicalPrint', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBetaPhysicalPrint', 'Physical print generated.', true);
        } catch {}
      },
    }),

    // Bulk Bid physical IPO applications
    // Endpoint: POST /api/v2/ipo/bid/bulk-applications
    submitBulkOnline: builder.mutation<BulkPhysicalSubmitResponse, FormData>({
      query: (body) => ({
        url: '/api/v2/ipo/bid/bulk-applications',
        method: 'POST',
        body,
        params: { source: 'ap' },
      }),
      transformErrorResponse: createApiErrorTransformer('IpoBetaBulkSubmit', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('IpoBetaBulkSubmit', 'Bulk physical bids submitted.', true);
        } catch {}
      },
    }),

    // Get user watchlist
    // Endpoint: GET /api/v2/ipo/watchlist
    getIpoBetaWatchlist: builder.query<
      any,
      { page?: number; limit?: number; source?: string; symbol?: string } | void
    >({
      query: (params) => ({
        url: `/api/v2/ipo/watchlist`,
        method: 'GET',
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 100,
          source: params?.source ?? 'ap',
          ...(params?.symbol ? { symbol: params.symbol } : {}),
        },
      }),
      providesTags: ['Watchlist'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('IpoBetaWatchlist', false),
    }),

    // Add IPO to watchlist
    // Endpoint: POST /api/v2/ipo/watchlist/add
    addToIpoBetaWatchlist: builder.mutation<any, { symbol: string }>({
      query: (body) => ({
        url: `/api/v2/ipo/watchlist/add`,
        method: 'POST',
        params: { source: 'ap' },
        body,
      }),
      invalidatesTags: ['Watchlist'],
      transformErrorResponse: createApiErrorTransformer('AddToWatchlist', false),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('AddToWatchlist', 'IPO added to Watchlist.', true);
        } catch {}
      },
    }),

    // Remove IPO from watchlist
    // Endpoint: POST /api/v2/ipo/watchlist/remove
    removeFromIpoBetaWatchlist: builder.mutation<any, { symbol: string }>({
      query: (body) => ({
        url: `/api/v2/ipo/watchlist/remove`,
        method: 'POST',
        params: { source: 'ap' },
        body,
      }),
      invalidatesTags: ['Watchlist'],
      transformErrorResponse: createApiErrorTransformer('RemoveFromWatchlist', false),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('RemoveFromWatchlist', 'IPO removed from Watchlist.', true);
        } catch {}
      },
    }),

    // Get IPO Transactions History (Online Bids)
    // Endpoint: GET /api/v2/ipo/transactions/history
    getIpoTransactionsHistory: builder.query<
      any,
      {
        source?: string;
        searchKey?: string;
        exchange?: string;
        sortColumn?: string;
        sortDirection?: string;
        pageNumber?: number;
        pageSize?: number;
        ipo_period?: string;
        startDate?: string;
        endDate?: string;
      } | void
    >({
      query: (params) => {
        const queryParams: Record<string, any> = {
          source: params?.source || 'ap',
          exchange: params?.exchange || 'ALL',
          sortColumn: params?.sortColumn || 'created_at',
          sortDirection: params?.sortDirection || 'DESC',
          pageNumber: params?.pageNumber ?? 1,
          pageSize: params?.pageSize ?? 10,
          ipo_period: params?.ipo_period || 'all',
        };
        if (params?.searchKey && params.searchKey.trim() !== '') {
          queryParams.searchKey = params.searchKey.trim();
        } else {
          queryParams.searchKey = '';
        }
        if (params?.startDate) queryParams.startDate = params.startDate;
        if (params?.endDate) queryParams.endDate = params.endDate;

        return {
          url: `/api/v2/ipo/transactions/history`,
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['BidReport'],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('TransactionsHistory', false),
    }),

    // Get Physical Print History
    // Endpoint: GET /api/v2/ipo/physical-print/history
    getIpoPhysicalPrintHistory: builder.query<
      any,
      {
        source?: string;
        searchKey?: string;
        exchange?: string;
        sortColumn?: string;
        sortDirection?: string;
        pageNumber?: number;
        pageSize?: number;
        ipo_period?: string;
        startDate?: string;
        endDate?: string;
      } | void
    >({
      query: (params) => {
        const queryParams: Record<string, any> = {
          source: params?.source || 'ap',
          exchange: params?.exchange || 'ALL',
          sortColumn: params?.sortColumn || 'created_at',
          sortDirection: params?.sortDirection || 'DESC',
          pageNumber: params?.pageNumber ?? 1,
          pageSize: params?.pageSize ?? 10,
          ipo_period: params?.ipo_period || 'all',
        };
        if (params?.searchKey && params.searchKey.trim() !== '') {
          queryParams.searchKey = params.searchKey.trim();
        } else {
          queryParams.searchKey = '';
        }
        if (params?.startDate) queryParams.startDate = params.startDate;
        if (params?.endDate) queryParams.endDate = params.endDate;

        return {
          url: `/api/v2/ipo/physical-print/history`,
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['BidReport'],
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('PhysicalPrintHistory', false),
    }),

    // Modify IPO Transaction
    // Endpoint: POST /api/v2/ipo/transactions/modify
    modifyIpoTransaction: builder.mutation<any, any>({
      query: (body) => ({
        url: `/api/v2/ipo/transactions/modify`,
        method: 'POST',
        params: { source: body?.source || 'ap' },
        body: body,
      }),
      invalidatesTags: ['BidReport'],
      transformErrorResponse: createApiErrorTransformer('ModifyIpoTransaction', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('ModifyIpoTransaction', 'IPO bid modified successfully.', false);
        } catch {}
      },
    }),

    // Cancel IPO Transaction
    // Endpoint: POST /api/v2/ipo/transactions/cancel
    cancelIpoTransaction: builder.mutation<any, any>({
      query: (body) => ({
        url: `/api/v2/ipo/transactions/cancel`,
        method: 'POST',
        params: { source: body?.source || 'ap' },
        body: body,
      }),
      invalidatesTags: ['BidReport'],
      transformErrorResponse: createApiErrorTransformer('CancelIpoTransaction', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('CancelIpoTransaction', 'IPO bid cancelled successfully.', false);
        } catch {}
      },
    }),
    // Download Physical IPO File (PDF/ZIP in base64)
    // Endpoint: GET /api/v2/ipo/physical-ipo/download?source=ap&id={id}
    downloadPhysicalIpoFile: builder.mutation<
      any,
      {
        id: number | string;
        source?: string;
      }
    >({
      query: ({ id, source = 'ap' }) => ({
        url: `/api/v2/ipo/physical-ipo/download`,
        method: 'GET',
        params: { source, id },
      }),
      transformResponse: (response: any) => response,
      transformErrorResponse: createApiErrorTransformer('DownloadPhysicalIpoFile', true),
    }),

    // Get physical IPO details
    // Endpoint: GET /api/v2/ipo/physical-ipo-details
    getPhysicalIpoDetails: builder.query<any, { status?: string }>({
      query: (params) => ({
        url: `/api/v2/ipo/physical-ipo-details`,
        method: 'GET',
        params: {
          source: 'ap',
          status: params?.status || 'all',
        },
      }),
      providesTags: ['IPO', 'IPODetails'],
      transformResponse: (response: any) => toCamelCase(response),
      transformErrorResponse: createApiErrorTransformer('GetPhysicalIpoDetails', false),
    }),

    // Download Physical IPO PDF (single)
    // Endpoint: POST /api/v2/ipo/get-ipo-physical-pdf
    downloadPhysicalIpoPdf: builder.mutation<
      any,
      {
        ipo: string;
        category: string;
        quantity: number;
        fetch_bank: boolean;
        client_id?: string;
        bo_id?: string;
      }
    >({
      query: (body) => ({
        url: `/api/v2/ipo/get-ipo-physical-pdf`,
        method: 'POST',
        params: { source: 'ap' },
        body,
      }),
      transformErrorResponse: createApiErrorTransformer('DownloadPhysicalIpoPdf', true),
    }),

    // Download Physical IPO PDF (bulk)
    // Endpoint: POST /api/v2/ipo/get-ipo-physical-pdf-bulk
    downloadPhysicalIpoPdfBulk: builder.mutation<any, FormData>({
      query: (body) => ({
        url: `/api/v2/ipo/get-ipo-physical-pdf-bulk`,
        method: 'POST',
        params: { source: 'ap' },
        body,
      }),
      transformErrorResponse: createApiErrorTransformer('DownloadPhysicalIpoPdfBulk', true),
    }),
  }),
});

export const {
  useGetIpoBetaDetailsMutation,
  useGetIpoBetaDetailsListQuery,
  useGetIpoBetaDetailsBySymbolMutation,
  useSubmitBidMutation,
  useAddIpoTransactionMutation,
  useAddBulkIpoTransactionCsvMutation,
  useAddMultipleIpoTransactionMutation,
  useGetMultipleIpoClientsQuery,
  useGetBidReportQuery,
  useUpdateBidStatusMutation,
  useGetClientDetailsQuery,
  useLazyGetClientDetailsQuery,
  useGetClientListQuery,
  useDownloadSampleCsvMutation,
  useGetPhysicalPrintMutation,
  useSubmitBulkOnlineMutation,
  useGetIpoBetaPerformanceAnalyticsQuery,
  useGetIpoBetaDashboardCountQuery,
  useGetIpoBetaSummaryDetailsQuery,
  useGetIpoBetaWatchlistQuery,
  useAddToIpoBetaWatchlistMutation,
  useRemoveFromIpoBetaWatchlistMutation,
  useGetIpoTransactionsHistoryQuery,
  useGetIpoPhysicalPrintHistoryQuery,
  useCancelIpoTransactionMutation,
  useModifyIpoTransactionMutation,
  useDownloadPhysicalIpoFileMutation,
  useGetIpoUpiListQuery,
  useAddIpoUpiMutation,
  useSetDefaultIpoUpiMutation,
  useDeleteIpoUpiMutation,
  useGetPhysicalIpoDetailsQuery,
  useDownloadPhysicalIpoPdfMutation,
  useDownloadPhysicalIpoPdfBulkMutation,
} = ipoBetaApi;
